import { defineStore } from "pinia"
import AtividadesRealizadasRepositorio from "@/repositorios/atividades/atividades.repositorio"
import { Estados, estados } from "@/types/api"
import { usarLeitorDeDados } from "@/composable/UsarLeitorDeDados/usarLeitorDeDados"
import { usarEventosPlanejadosStore } from "../EventosPlanejados/EventosPlanejados.store"
import {
  AtividadePlanejada,
  ParametrosAtividadePlanejada,
} from "@/repositorios/eventos/eventos.modelo"
import {
  filtrarEMapearAtividadesQuePodemSerIniciadas,
  filtrarEMapearEventosQueAindaNaoPodemSerIniciados,
  filtrarEMapearAtividadesQueAindaNaoPodemSerIniciadas,
  tratarErrosDeRequisicao,
  definirCacheNosLeitores
} from "./ListarAtividades.script"
import { Atividade, AtividadesParams } from "@/repositorios/atividades/atividades.modelos"
import {
  ModeloEventoCalendario,
  ModeloEventoCalendarioPodeSerIniciada,
} from ".."
import i18n from "@/i18n"

interface AtividadesStore {
  atividades: ModeloEventoCalendario[] | ModeloEventoCalendarioPodeSerIniciada[]
  atividadesRequisitadas: ModeloEventoCalendario[] | ModeloEventoCalendarioPodeSerIniciada[]
  estagioDaRequisicao: Estados
}

const atividadesRealizadasRep = new AtividadesRealizadasRepositorio({
  utilizaCache: true,
})
const { leitor } = usarLeitorDeDados()

const { t } = i18n.global

const lerAtividadesRealizadas = leitor<Atividade, AtividadesParams>(
  atividadesRealizadasRep.listarAtividadesRealizadasFrontend,
  {}
)

export const usarListarAtividadesStore = defineStore("listar-atividades", {
  state: (): AtividadesStore => ({
    atividades: [],
    atividadesRequisitadas: [],
    estagioDaRequisicao: estados.INICIAL,
  }),
  getters: {
    carregandoAtividades: (state) => state.estagioDaRequisicao === 1,
    atividadesComOcorrencias: (state) => {
      return state.atividades.filter((atividade) =>
        Boolean(atividade.problemas)
      )
    },
  },
  actions: {
    async verTodasAsAtividades(
      parametros: AtividadesParams = { "por-pagina": "1000" },
      utilizarCache = true
    ) {
      definirCacheNosLeitores(
        lerAtividadesRealizadas,
        utilizarCache
      )
      const eventosPlanejadosStore = usarEventosPlanejadosStore()

      this.estagioDaRequisicao = estados.PEDENTE
      lerAtividadesRealizadas.sobrescreverParametros(parametros)

      const parametrosAtividadesPlanejadas: ParametrosAtividadePlanejada = {
        pagina: parametros.pagina,
        "por-pagina": parametros["por-pagina"],
        atividade_do_projeto_id: parametros.id_atividade_do_projeto,
        profissionais_alocados: parametros.id_profissional_que_realizou,
      }

      await Promise.all([
        lerAtividadesRealizadas.executar(),
        eventosPlanejadosStore.verEventosPlanejados(
          parametrosAtividadesPlanejadas,
          utilizarCache
        ),
      ])

      const existeErroNasRequisicoes = tratarErrosDeRequisicao({
        statusAtividades: lerAtividadesRealizadas.resultado.status,
        statusEventosPlanejados: eventosPlanejadosStore.statusEventoPlanejados,
      })

      if (existeErroNasRequisicoes === "erro") {
        this.estagioDaRequisicao = estados.ERRO
        throw new Error(t("erroAoCarregarEventosDoCalendario"))
      }
      this.estagioDaRequisicao = estados.SUCESSO

      const atividadesRealizadasNaoDeletadas: Atividade[] =
        lerAtividadesRealizadas.resultado.status === "sucesso"
          ? lerAtividadesRealizadas.resultado.dados
          : []

      const eventosPlanejados: AtividadePlanejada[] =
        eventosPlanejadosStore.eventosPlanejados

      const atividadesQuePodemSerIniciadas =
        filtrarEMapearAtividadesQuePodemSerIniciadas(
          atividadesRealizadasNaoDeletadas,
          eventosPlanejados
        )
      const eventos =
        filtrarEMapearEventosQueAindaNaoPodemSerIniciados(eventosPlanejados)
      const atividades = filtrarEMapearAtividadesQueAindaNaoPodemSerIniciadas(
        atividadesRealizadasNaoDeletadas,
        atividadesQuePodemSerIniciadas
      )

      const todasAsAtividades = [
        ...atividadesQuePodemSerIniciadas,
        ...eventos,
        ...atividades,
      ]

      
      this.atividadesRequisitadas = [...todasAsAtividades]
      this.atividades = [...todasAsAtividades]
    },

    limparAtividades() {
      this.atividades = []
    },
  },
})
